import type { AdditionalContact, RootScope } from '../types';
import type { CustomField, RichLead } from './types';

import angular from 'angular';

import {
  appealSourceApi,
  leadApi,
  phoneApi,
  projectApi,
  userApi,
} from '../services/api';
import { contactsChecker } from '../services/contactsChecker';
import { intlTel } from '../services/intlTel';
import { notify } from '../ui';
import { redirectToLead } from './redirectToLead';
import { logger } from '../services/logger';

type LeadViewModel = Omit<
  RichLead,
  'additional_contacts' | 'objects' | 'tags'
> & {
  isEdit?: boolean;
  additional_contacts: ContactViewModel[] | null;
  noteHashtag?: string;
  noteWithoutHashtag?: string;
  objects:
    | RichLead['objects']
    | {
        object_id: string;
        object: {
          id: string;
          name: string;
        };
      }[];
  tags:
    | RichLead['tags']
    | {
        tag_id: string;
        tag: {
          id: string;
          name: string;
        };
      }[];
};
type ContactViewModel = AdditionalContact & { isEdit?: boolean };

interface SelectOptions {
  name: string;
  id: string;
  selected: boolean;
}

export class LeadPopupCtrl {
  static $inject = [
    '$scope',
    '$stateParams',
    '$state',
    '$rootScope',
    '$location',
    '$translate',
  ];

  linksToUnits = '';
  textRoom = '';
  textSqm = '';
  textReservation = '';

  loaded = false;
  showEditSegmentation = false;
  showAddNote = false;
  showEditCustomFields = false;
  showAddContact = false;
  appealSourceName = '';

  ringostatEnabled = false;

  /**
   * Whether we are waiting for a call request from backend
   */
  makingCall = false;

  /**
   * Current user contacts to use for starting calls
   */
  userContacts = {
    normal: '',
    sip: '',
  };

  /**
   * Maps a raw phone values from lead data to a normalized version
   */
  leadPhoneMap: { [key: string]: string } = {};
  declare fallbackCountry: string;

  custom_fields: CustomField[] = [];
  projects: SelectOptions[] = [];
  itemsOfInterest: SelectOptions[] = [];

  declare leadId: string;
  declare lead: LeadViewModel;
  declare editLead: LeadViewModel | undefined;
  declare editAdditionalContact: ContactViewModel | undefined;
  declare customFieldsToEdit: { [key: string]: string } | undefined;

  /**
   * Holds a model for angular form
   */
  declare contact: Partial<ContactViewModel>;

  /**
   * isteven-multi-select output model
   */
  declare selectedProjects: SelectOptions[];

  /**
   * isteven-multi-select output model
   */
  declare selectedItems: SelectOptions[];

  constructor(
    private $scope: angular.IScope,
    $stateParams: { id: string },
    private $state,
    private $rootScope: RootScope,
    private $location: angular.ILocationService,
    private $translate: angular.translate.ITranslateService,
  ) {
    this.textRoom = $translate.instant('lead.popup.links.room');
    this.textSqm = $translate.instant('lead.popup.links.sqm');
    this.textReservation = $translate.instant('lead.popup.links.reservation');

    $rootScope.$on('$translateChangeSuccess', () => {
      this.textRoom = $translate.instant('lead.popup.links.room');
      this.textSqm = $translate.instant('lead.popup.links.sqm');
      this.textReservation = $translate.instant('lead.popup.links.reservation');
    });

    this.leadId = $stateParams.id;
    this.fallbackCountry = $rootScope.getDefaultCountry();

    this.init();
  }

  async init() {
    this.lead = {} as any;

    try {
      await Promise.all([
        leadApi.get(this.leadId).then(async (lead) => {
          if (!lead) {
            return;
          }

          // normalize all phone values to ensure that we will send correct data
          // when communicating with API
          const phoneMap = {};
          const allPhones = [
            lead.phone,
            ...(lead.contacts || []).map((item) => item.value),
            ...(lead.additional_contacts || []).flatMap((item) =>
              [String(item.phone)].concat(
                (item.contacts || []).map((contact) => contact.value),
              ),
            ),
          ].filter(Boolean);

          for (const phone of allPhones) {
            const formattedPhone = await intlTel.parseNumber(phone, {
              country: this.fallbackCountry,
            });

            if (!formattedPhone) {
              continue;
            }

            phoneMap[phone] = formattedPhone?.replace(/^\+/, '');
          }

          this.$scope.$apply(() => {
            this.lead = lead;
            this.leadPhoneMap = phoneMap;
            this.loaded = true;
            this.createHashtag();

            this.populateLinksToUnits();
            this.resolveAppealSourceName();
          });
        }),

        leadApi.getCustomFields().then((customFields) => {
          this.custom_fields = customFields;
        }),

        projectApi.getAll().then((projects) => {
          this.projects = projects.map((project) => ({
            name: project.name,
            id: project.id,
            selected: false,
          }));
        }),

        leadApi.getTags().then((tags) => {
          this.itemsOfInterest = tags.map((tag) => ({
            name: tag.name,
            id: tag.id,
            selected: false,
          }));
        }),
      ]);

      phoneApi
        .isRingostatEnabled()
        .then(async (isEnabled) => {
          if (!isEnabled) {
            return;
          }

          const { phone, sip } = await userApi.getMe();

          this.$scope.$apply(() => {
            this.ringostatEnabled = isEnabled;
            this.userContacts = {
              normal: phone || '',
              sip: sip || '',
            };
          });
        })
        .catch((error) => {
          logger.error('Error initializing call request feature', {
            error,
          });
        });

      this.$scope.$apply();
    } catch (error) {
      logger.error('Error initializing lead popup', {
        error,
      });
    }
  }

  async resolveAppealSourceName() {
    if (this.lead.ad_source_id) {
      appealSourceApi
        .getAll()
        .then(({ ad_source: sources }) => {
          const source = sources.find(
            (item) => item.id === this.lead.ad_source_id,
          );

          if (!source) {
            return;
          }

          this.appealSourceName = source?.name_local;
          this.$scope.$apply();
        })
        .catch((error) => {
          logger.error('Error resolving appeal source name', {
            ad_source_id: this.lead.ad_source_id,
            error,
          });
        });
    } else {
      this.appealSourceName = '';
    }
  }

  closeLeadPopup = () => {
    // need to use context binding because this function will be called from outside
    this.$state.go('^');
  };

  redirectToUnit(unitId: string) {
    if (
      this.$location.absUrl().indexOf('/section') >= 0 ||
      this.$location.absUrl().indexOf('/house') >= 0
    ) {
      this.$state.go('unitpopup.view', { id: unitId });
    } else {
      this.$state.go('leads.unitpopup', { id: unitId });
    }
  }

  editLeadClick(lead: LeadViewModel) {
    lead.isEdit = !lead.isEdit;
    this.editLead = angular.copy(lead);
  }

  async saveLead(lead: LeadViewModel) {
    try {
      await leadApi.update(lead.id, lead);

      this.$scope.$apply(() => {
        lead.isEdit = false;
        this.lead = lead;

        // update values for Lead.note rendering
        this.createHashtag();
      });

      this.$rootScope.$broadcast('activity.update');
    } catch (error) {
      notify.error('Error updating lead', error, {
        lead,
      });
    }
  }

  cancelEditLead(lead: LeadViewModel) {
    lead.isEdit = false;
  }

  editCustomFields() {
    this.showEditCustomFields = !this.showEditCustomFields;

    this.customFieldsToEdit = angular.copy(this.lead.custom_fields);
  }

  async saveCustomFields(customFields) {
    const lead = angular.copy(this.lead);
    lead.custom_fields = customFields;

    try {
      // TODO: we can merge this with saveLead() method after checking that this is safe
      await leadApi.update(lead.id, lead);

      this.$scope.$apply(() => {
        this.lead = lead;
        this.showEditCustomFields = false;
      });

      this.$rootScope.$broadcast('activity.update');
    } catch (error) {
      logger.error('Error updating lead custom fields', {
        error,
        lead,
      });
    }
  }

  cancelCustomFields() {
    this.showEditCustomFields = false;
  }

  addContact() {
    this.showAddContact = !this.showAddContact;
    this.contact = {};
  }

  async addNewContact(contact: AdditionalContact) {
    try {
      const newContact = await leadApi.createAdditionalContact(
        this.leadId,
        contact,
      );

      this.$scope.$apply(() => {
        if (!this.lead.additional_contacts) {
          this.lead.additional_contacts = [];
        }

        this.lead.additional_contacts.push(newContact);
        this.showAddContact = false;
      });
    } catch (error) {
      logger.error('Error creating additional contact', {
        error,
        contact,
      });
    }
  }

  cancelAddContact() {
    this.showAddContact = false;
  }

  editContactClick(contact: ContactViewModel) {
    if (!this.lead.additional_contacts) {
      return;
    }

    this.lead.additional_contacts.forEach((additionalContact) => {
      additionalContact.isEdit = false;
    });

    contact.isEdit = true;

    this.editAdditionalContact = angular.copy(contact);
  }

  async saveContact(index: number, contact: ContactViewModel) {
    delete contact.isEdit;

    await leadApi.updateAdditionalContact(this.leadId, {
      contact,
      index,
    });

    this.$scope.$apply(() => {
      if (!this.lead.additional_contacts) {
        this.lead.additional_contacts = [];
      }

      this.lead.additional_contacts[index] = contact;
    });
  }

  cancelEditContact(contact: ContactViewModel) {
    contact.isEdit = false;
  }

  hasLink(str: string) {
    return contactsChecker.hasLink(str);
  }

  createHashtag() {
    const hasTagMatch = String(this.lead.note).match(/#[^# ]+/);

    this.lead.noteHashtag = hasTagMatch ? hasTagMatch[0] : '';

    this.lead.noteWithoutHashtag = this.lead.note
      .replace(this.lead.noteHashtag, '')
      .trim();
  }

  setProjectsAndTagsSelected() {
    this.lead.objects.forEach((obj) => {
      this.projects.forEach((proj) => {
        if (proj.id === obj.object_id) {
          proj.selected = true;
        }
      });
    });

    this.lead.tags.forEach((tag) => {
      this.itemsOfInterest.forEach((item) => {
        if (item.id === tag.tag_id) {
          item.selected = true;
        }
      });
    });
  }

  async saveSegmentation() {
    const lead = angular.copy(this.lead);

    try {
      // TODO: we can merge this with saveLead() method after checking that this is safe
      await leadApi.update(lead.id, {
        ...lead,
        segmentation_objects: this.selectedProjects.map((item) => ({
          [item.id]: item.name,
        })),
        segmentation_tags: this.selectedItems.map((item) => ({
          [item.id]: item.name,
        })),
      });

      this.resolveAppealSourceName();

      this.$scope.$apply(() => {
        lead.objects = this.selectedProjects.map((item) => ({
          object_id: item.id,
          object: { id: item.id, name: item.name },
        }));

        lead.tags = this.selectedItems.map((item) => ({
          tag_id: item.id,
          tag: { id: item.id, name: item.name },
        }));

        this.lead = lead;
        this.showEditSegmentation = false;
      });
    } catch (error) {
      logger.error('Error updating lead segmentation', {
        error,
        lead,
      });
    }
  }

  populateLinksToUnits() {
    if (!this.lead.lead_unit_ass) {
      return;
    }

    const [companySlug] = this.$location.host().split('.');

    if (this.lead.lead_unit_ass.interested.length) {
      this.lead.lead_unit_ass.interested.forEach((item) => {
        this.linksToUnits +=
          `${item.object_name} / ${item.tower} / #${item.num} ··· ${item.rooms}${this.textRoom} ${item.total_area} ${this.textSqm} ··· ` +
          `https://${companySlug}.estoffice.com/units/share/tmpl1/${item.unit_id}\n`;
      });
      this.linksToUnits += '\n';
    }

    if (this.lead.lead_unit_ass.booking.length) {
      this.linksToUnits += `${this.textReservation}:\n`;

      this.lead.lead_unit_ass.booking.forEach((item) => {
        this.linksToUnits +=
          `${item.object_name} / ${item.tower} / #${item.num} ··· ${item.rooms}${this.textRoom} ${item.total_area} ${this.textSqm} ··· ` +
          `https://${companySlug}.estoffice.com/units/share/tmpl1/${item.unit_id}\n`;
      });
    }

    this.linksToUnits = this.linksToUnits.replace(/\n+$/, '');
  }

  canMakePhoneCall(
    phone: string,
    callType: 'normal' | 'sip' = 'normal',
  ): boolean {
    return (
      this.ringostatEnabled &&
      !!this.leadPhoneMap[phone] &&
      !!this.userContacts[callType]
    );
  }

  async makePhoneCall(phone: string, callType: 'normal' | 'sip' = 'normal') {
    const from = this.userContacts[callType];
    const to = this.leadPhoneMap[phone];

    if (!from || !to || this.makingCall) {
      return;
    }

    this.makingCall = true;

    try {
      await phoneApi.performCall({
        from,
        to,
      });

      notify.success(
        this.$translate.instant('lead.call.starting.from.to', {
          from,
          to,
        }),
      );
    } catch (error) {
      const errorCodeDesc =
        {
          0: 'MSG_ERR_NONE',
          1: 'MSG_ERR_CALL_NOT_SUPPORTED',
          2: 'MSG_ERR_CALL_PARAM_FROM',
          3: 'MSG_ERR_CALL_PARAM_TO',
          4: 'MSG_ERR_CALL_UNSUCCESSFUL',
          5: 'MSG_ERR_PROVIDER_ERROR',
        }[error.code] || `UNKNOWN_${error.code}`;

      notify.error(`Error performing call (${errorCodeDesc})`, error, {
        error,
        from,
        to,
        phone,
        callType,
        errorCodeDesc,
      });
    } finally {
      this.makingCall = false;
    }
  }

  /**
   * Opens contextual menu for lead
   */
  openMenu($mdMenu: angular.material.IMenuService, event: MouseEvent) {
    $mdMenu.open(event);
  }

  async onLeadMerge({
    contactType,
    destinationLead: { originalObject: destinationLead },
  }: {
    contactType: 'main' | 'additional';
    destinationLead: {
      originalObject: {
        id: string;
        name: string;
        phone: string;
        email: string;
        note: string;
      };

      // fields from angucomplete
      title: string;
      image: string;
      // same as originalObject
      description: { [key: string]: any };
    };
  }) {
    if (this.lead.id === destinationLead.id) {
      return;
    }

    try {
      await leadApi.merge({
        contactType,
        sourceId: this.lead.id,
        destinationId: destinationLead.id,
      });

      redirectToLead({
        leadId: destinationLead.id,
        $state: this.$state,
        $location: this.$location,
      });

      notify.success(
        this.$translate.instant('lead.merge.successfully.completed'),
      );
    } catch (error) {
      notify.error(
        this.$translate.instant('lead.merge.error.merging.leads'),
        error,
        {
          message: 'Error merging leads',
          sourceLead: this.lead,
          destinationLead,
        },
      );
    }
  }
}
